import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Zap, LogOut, Crown, User, Clock, Brain, FileText, ArrowRight, AlertTriangle, BarChart3, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPage() {
  const { user, logout, getAuthHeaders } = useAuth();
  const navigate = useNavigate();
  const [decisions, setDecisions] = useState([]);
  const [simulations, setSimulations] = useState({});
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const headers = getAuthHeaders();
      const [decisionsRes, usageRes] = await Promise.all([
        axios.get(`${API}/decisions`, { headers, withCredentials: true }),
        axios.get(`${API}/user/usage`, { headers, withCredentials: true })
      ]);
      setDecisions(decisionsRes.data);
      setUsage(usageRes.data);

      // Check which decisions have completed simulations
      const simStatus = {};
      for (const d of decisionsRes.data) {
        try {
          const simRes = await axios.get(`${API}/simulations/${d.decision_id}`, { headers, withCredentials: true });
          simStatus[d.decision_id] = simRes.data?.status === 'completed' ? 'completed' : 'incomplete';
        } catch {
          simStatus[d.decision_id] = 'none';
        }
      }
      setSimulations(simStatus);
    } catch (err) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleDelete = async (e, decisionId) => {
    e.stopPropagation(); // Prevent navigation to simulation page
    if (!window.confirm('Are you sure you want to delete this incomplete decision?')) return;

    setDeleting(decisionId);
    try {
      const headers = getAuthHeaders();
      await axios.delete(`${API}/decisions/${decisionId}`, { headers, withCredentials: true });
      setDecisions(decisions.filter(d => d.decision_id !== decisionId));
      toast.success('Decision deleted');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete decision');
    } finally {
      setDeleting(null);
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  const modeIcons = {
    career: '💼', education: '🎓', relationship: '❤️', health: '🏋',
    financial: '💰', custom: '📝', daily: '⏰'
  };

  const canSimulate = usage?.simulations_remaining === -1 || (usage?.simulations_remaining || 0) > 0;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#050505' }}>
        <div className="w-8 h-8 border-2 rounded-full animate-spin" style={{ borderColor: '#6366F1', borderTopColor: 'transparent' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: '#050505' }}>
      {/* Top Nav */}
      <nav className="glass-heavy sticky top-0 z-50" data-testid="dashboard-nav">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3 cursor-pointer" onClick={() => navigate('/')}>
            <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: '#6366F1' }}>
              <Zap className="w-4 h-4 text-white" />
            </div>
            <span className="font-bold text-lg tracking-tight" style={{ fontFamily: 'Syne' }}>Regret Engine</span>
          </div>
          <div className="flex items-center gap-3">
            {usage?.tier === 'free' && (
              <Button data-testid="upgrade-btn" onClick={() => navigate('/upgrade')} variant="outline" className="rounded-full px-4 py-2 text-xs border-yellow-500/30 text-yellow-400 hover:bg-yellow-500/10">
                <Crown className="w-3 h-3 mr-1" /> Buy Credits
              </Button>
            )}
            <Button data-testid="profile-btn" onClick={() => navigate('/profile')} variant="ghost" className="rounded-full p-2 hover:bg-white/5">
              <User className="w-5 h-5" style={{ color: '#94A3B8' }} />
            </Button>
            <Button data-testid="logout-btn" onClick={handleLogout} variant="ghost" className="rounded-full p-2 hover:bg-white/5">
              <LogOut className="w-5 h-5" style={{ color: '#94A3B8' }} />
            </Button>
          </div>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 py-10">
        {/* Welcome */}
        <div className="mb-10">
          <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Syne' }} data-testid="dashboard-welcome">
            Welcome back, {user?.name?.split(' ')[0] || 'there'}
          </h1>
          <p className="text-sm" style={{ color: '#94A3B8' }}>Your decision simulation dashboard.</p>
        </div>

        {/* IP Warning */}
        {usage?.ip_warning && (
          <div className="glass-card rounded-xl p-4 mb-6 flex items-center gap-3" style={{ borderColor: 'rgba(239,68,68,0.3)' }} data-testid="ip-warning">
            <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: '#EF4444' }} />
            <p className="text-sm" style={{ color: '#EF4444' }}>
              Suspicious activity detected from your network. Creating multiple accounts to bypass free tier limits may result in a permanent ban.
            </p>
          </div>
        )}

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10" data-testid="stats-grid">
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(99,102,241,0.15)' }}>
                <BarChart3 className="w-5 h-5" style={{ color: '#6366F1' }} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Simulations</p>
                <p className="text-xl font-bold" style={{ fontFamily: 'Syne' }}>
                  {usage?.simulations_remaining === -1 ? 'Unlimited' : (usage?.simulations_remaining || 0)}
                </p>
                {usage?.simulations_remaining !== -1 && <p className="text-xs" style={{ color: '#475569' }}>remaining</p>}
              </div>
            </div>
            {usage?.tier === 'free' && !canSimulate && (
              <Button data-testid="stats-upgrade-btn" onClick={() => navigate('/upgrade')} className="w-full rounded-lg text-xs py-2" style={{ background: '#6366F1' }}>
                Buy Decision Credits
              </Button>
            )}
          </div>
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(16,185,129,0.15)' }}>
                <FileText className="w-5 h-5" style={{ color: '#10B981' }} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Decisions</p>
                <p className="text-xl font-bold" style={{ fontFamily: 'Syne' }}>{usage?.total_decisions || 0}</p>
              </div>
            </div>
          </div>
          <div className="glass-card rounded-xl p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: 'rgba(139,92,246,0.15)' }}>
                <Crown className="w-5 h-5" style={{ color: '#8B5CF6' }} />
              </div>
              <div>
                <p className="text-xs uppercase tracking-widest" style={{ color: '#475569' }}>Paid Credits</p>
                <p className="text-xl font-bold" style={{ fontFamily: 'Syne' }}>
                  {usage?.tier === 'premium' ? 'Unlimited' : (usage?.simulation_credits || 0)}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* New Decision Button */}
        <div className="mb-10">
          <Button data-testid="new-decision-btn" onClick={() => {
            if (!canSimulate) { toast.error('Purchase a Decision Pack to run another simulation'); navigate('/upgrade'); return; }
            navigate('/decision/new');
          }} className="rounded-full px-8 py-6 text-base font-semibold glow-primary-hover transition-transform hover:-translate-y-1"
            style={{ background: '#6366F1', boxShadow: '0 0 20px rgba(99,102,241,0.3)' }}>
            <Plus className="w-5 h-5 mr-2" /> New Decision Simulation
          </Button>
        </div>

        {/* Past Decisions */}
        <div>
          <h2 className="text-lg font-semibold mb-6" style={{ fontFamily: 'Syne' }} data-testid="past-decisions-title">
            Your Decisions
          </h2>
          {decisions.length === 0 ? (
            <div className="glass-card rounded-xl p-12 text-center" data-testid="no-decisions">
              <Brain className="w-12 h-12 mx-auto mb-4" style={{ color: '#475569' }} />
              <p className="text-sm mb-4" style={{ color: '#94A3B8' }}>No decisions yet. Start your first simulation!</p>
              <Button data-testid="first-decision-btn" onClick={() => navigate('/decision/new')} className="rounded-full px-6 text-sm" style={{ background: '#6366F1' }}>
                <Plus className="w-4 h-4 mr-1" /> Create Your First Decision
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {decisions.map((d) => {
                const simStatus = simulations[d.decision_id];
                const isIncomplete = simStatus === 'none' || simStatus === 'incomplete';

                return (
                  <div key={d.decision_id} className="glass-card rounded-xl p-6 cursor-pointer transition-colors hover:border-white/10 group relative"
                    onClick={() => navigate(`/simulation/${d.decision_id}`)} data-testid={`decision-card-${d.decision_id}`}>

                    {/* Delete button for incomplete simulations */}
                    {isIncomplete && (
                      <button
                        onClick={(e) => handleDelete(e, d.decision_id)}
                        disabled={deleting === d.decision_id}
                        className="absolute top-3 right-3 p-2 rounded-lg transition-colors hover:bg-red-500/20 z-10"
                        title="Delete incomplete decision"
                        style={{ color: '#EF4444' }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}

                    <div className="flex items-start justify-between mb-3">
                      <span className="text-2xl">{modeIcons[d.mode] || '📋'}</span>
                      <div className="flex items-center gap-2">
                        {isIncomplete && (
                          <span className="text-xs px-2 py-1 rounded-full" style={{ background: 'rgba(245,158,11,0.15)', color: '#F59E0B' }}>
                            Incomplete
                          </span>
                        )}
                        <span className="text-xs px-2 py-1 rounded-full capitalize" style={{ background: 'rgba(99,102,241,0.15)', color: '#6366F1' }}>
                          {d.mode}
                        </span>
                      </div>
                    </div>
                    <h3 className="font-semibold text-sm mb-2 truncate" style={{ fontFamily: 'Syne' }}>{d.title}</h3>
                    <p className="text-xs mb-4 line-clamp-2" style={{ color: '#94A3B8' }}>{d.description}</p>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-1 text-xs" style={{ color: '#475569' }}>
                        <Clock className="w-3 h-3" />
                        {new Date(d.created_at).toLocaleDateString()}
                      </div>
                      <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" style={{ color: '#6366F1' }} />
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
